import type { ChartSelectorConfig, SlotRole } from '@revelio/core';
import type { PlotBreakdownLevels } from '@revelio/core/global-ai-assistant/breakdown-levels';
import type { ChordTickLabelMode, FormatType } from '@revelio/replots';

import type { FilterId } from '../../../filters/types/filters';
import type { ChartType } from './chart';
import {
  type CompositionSecondaryDimensionValues,
  isCompositionSecondaryDimension,
  type SecondaryDimensionValues,
} from './dimension';
import type { MetricValues } from './metric';

export type Position = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type DivergingBarComboSortKey = 'left' | 'right' | 'diff' | 'sum';

type DivergingBarComboSort = {
  by: DivergingBarComboSortKey;
  direction?: 'asc' | 'desc';
};

export enum PlotFilterType {
  TOP_N = 'TOP_N',
  BOTTOM_N = 'BOTTOM_N',
}

type TopNFilter = {
  type: PlotFilterType.TOP_N;
  value: number;
};

type BottomNFilter = {
  type: PlotFilterType.BOTTOM_N;
  value: number;
};

export type PlotFilter = TopNFilter | BottomNFilter;

export enum DimensionType {
  PRIMARY_DIMENSION = 'PRIMARY_DIMENSION',
  TOP_SEGMENTS = 'TOP_SEGMENTS',
}

export type DimensionSource = SecondaryDimensionValues | 'primary';

export type DataNormalization = 'none' | 'area_density';
export type DataTransformation = 'none' | 'sqrt' | 'log';
export type PlotSortDirection = 'ASC' | 'DESC';

export type MapPlotOptions = {
  normalization?: DataNormalization;
  transformation?: DataTransformation;
};

export type SankeyWidthScale = 'linear' | 'power';

export type SankeyPlotOptions = {
  widthScale?: SankeyWidthScale;
  totalExponent?: number;
  shareExponent?: number;
  leftFlowLabel?: string;
  rightFlowLabel?: string;
};

export type PlotParameter = {
  parameterId: string;
  grouped: boolean;
};

export type SnowflakeFieldType = 'string' | 'number' | 'string[]' | 'number[]';

export type ExtractMode = 'id' | 'levelName';

type SnowflakeConfigSubField = {
  name: string;
  fieldType: SnowflakeFieldType;
  selectionLevelIds: string[];
  extractMode?: ExtractMode;
};

export type FieldBindingMode =
  | 'static'
  | 'dashboard_filter'
  | 'report_parameter';

export type SnowflakeConfigField = {
  name: string;
  label: string;
  value: string;
  required?: boolean;
  fieldType?: SnowflakeFieldType;
  /** When set, field value is resolved from this report parameter's current selections at execution time. */
  parameterId?: string;
  /** When set, only tree selections whose levelId is in this list are included. */
  selectionLevelIds?: string[];
  /** When present, this field is a group that expands into multiple query fields. */
  subFields?: SnowflakeConfigSubField[];
  /** Semantic role: which standard selector slot this field binds to. */
  slotRole?: SlotRole;
  /** How this field's value is sourced at query time. */
  bindingMode?: FieldBindingMode;
  /** When bindingMode is 'dashboard_filter', the FilterId of the bound dashboard filter. */
  boundFilterId?: string;
  /** From saved-query metadata: which report-parameter filter types this field may bind to. */
  compatibleFilterIds?: FilterId[];
};

export type SnowflakeConfig = {
  savedQueryId: string;
  fields: SnowflakeConfigField[];
};

export type DataSourceType = 'mw-gql' | 'snowflake';

type TableRowPopoutOptions = {
  chartType?: ChartType;
};

export type ComparisonBaseline = 'first' | 'second';

export type ComparisonTransformation = {
  /** Which entity is the baseline (comparison/reference). The other is the focus. In diverging views, baseline is left (grey), focus is right (blue). */
  baseline?: ComparisonBaseline;
};

type ColumnAggregation = 'sum' | 'avg' | 'min' | 'max' | 'count';
export type ColumnAlignment = 'left' | 'center' | 'right';

/** A column that renders an aggregated metric value. */
type TableVariableColumn = {
  id: string;
  kind: 'variable';
  metric: MetricValues;
  aggregation?: ColumnAggregation;
  label?: string;
  format?: FormatType;
};

/** A column that renders a dimension value (grouping key for rows). */
type TableDimensionColumn = {
  id: string;
  kind: 'dimension';
  dimension: SecondaryDimensionValues;
  alignment?: ColumnAlignment;
  label?: string;
};

export type TableColumnConfig = TableDimensionColumn | TableVariableColumn;

export type TreemapTaxonomyCoverage = {
  topN?: { groups: number; cells: number };
  omitOther?: boolean;
};

export type SubsidiarySnapshotKind = 'concentration' | 'activity';

export type SubsidiaryTrendKind = 'count' | 'concentration' | 'activity';

type IciclePlotOptions = {
  sizeTransform?: Exclude<DataTransformation, 'log'>;
};

export type PlotOptions = {
  chartType?: ChartType;
  secondaryDimension?: SecondaryDimensionValues;
  /**
   * Explicit breakdown granularity per dimension (today: geography only).
   * Overrides the drill-down default in getCompositionV3BreakdownOptions —
   * set deterministically by the AI share pipeline from the pruned segment's
   * kind, so "share in the US" charts countries, not regions.
   */
  breakdownLevels?: PlotBreakdownLevels;
  shareOfSegment?: boolean;
  groupBySegment?: boolean;
  adaptive?: boolean;
  plotFilters?: PlotFilter[];
  dimensionSource?: DimensionSource;
  defaultMetricOptions?: MetricValues[];
  tableColumns?: TableColumnConfig[];
  primaryEntitySwitcher?: boolean;
  /** Override default chart selector visibility (e.g., from saved queries) */
  selectorOverrides?: Partial<ChartSelectorConfig>;
  color?: string;
  colorPalette?: string[];
  showDataLabels?: boolean;
  sort?: PlotSortDirection | null;
  maxValue?: number;
  minValue?: number;
  showZebraStripe?: boolean;
  showQuartiles?: boolean;
  enableRowSelection?: boolean;
  imageSrc?: string;
  plotParameters?: PlotParameter[];
  /** HTML content for 'text' chart type */
  textHtml?: string;
  /** Prompt for client-side text assist (report AI gen); when set without textHtml, client fetches template */
  textPrompt?: string;
  /** Target size for text content (small/medium/large/fullPage); used by text-assist for length guidance */
  contentSize?: 'small' | 'medium' | 'large' | 'fullPage';
  /** Vertical alignment for 'text' chart content */
  textVAlign?: 'start' | 'center' | 'end';
  /** Exclude this text chart from executive summary generation */
  excludeFromExecutiveSummary?: boolean;
  mapOptions?: MapPlotOptions;
  tableRowPopout?: TableRowPopoutOptions;
  comparisonTransformation?: ComparisonTransformation;
  divergingBar?: {
    arrowOrientation?: 'horizontal' | 'vertical';
    positiveLabel?: string;
    negativeLabel?: string;
  };
  divergingBarCombo?: {
    comparisonLabel?: string;
    differenceLabel?: string;
    leftHeaderLabel?: string;
    rightHeaderLabel?: string;
    sort?: DivergingBarComboSort;
    subfilterDimension?: CompositionSecondaryDimensionValues;
    dynamicEntityComparison?: boolean;
  };
  displayAs?: 'value' | 'share' | 'auto';
  treemap?: {
    sizeTransform?: Exclude<DataTransformation, 'log'>;
    taxonomyCoverage?: TreemapTaxonomyCoverage;
  };
  icicle?: IciclePlotOptions;
  subsidiarySnapshot?: SubsidiarySnapshotKind;
  subsidiaryTrend?: SubsidiaryTrendKind;
  sankey?: SankeyPlotOptions;
  chord?: {
    showTickLabels?: boolean;
    tickLabelMode?: ChordTickLabelMode;
  };
  gaugeV2?: {
    value?: string;
    min?: string;
    max?: string;
    minLabel?: string;
    maxLabel?: string;
  };
  showLegend?: boolean;
  /** Line chart: Show diff label between first two series */
  showDiffLabel?: boolean;
  /** Line chart: Y-axis minimum value (supports handlebars templates) */
  yAxisMin?: string;
  /** Line chart: Y-axis maximum value (supports handlebars templates) */
  yAxisMax?: string;
  curveSmoothness?: number;
  fontSize?: number;
  /** Distribution chart: CDF lower cutoff (0–100), default 1 */
  distributionCutoffLower?: number;
  /** Distribution chart: CDF upper cutoff (0–100), default 99 */
  distributionCutoffUpper?: number;
  format?: FormatType;
  /** Grouped bar chart: bar direction, default horizontal */
  orientation?: 'horizontal' | 'vertical';
  /** Data source — mw-gql (default) or snowflake custom query */
  dataSource?: DataSourceType;
  snowflakeConfig?: SnowflakeConfig;
  hideOther?: boolean;
};

export const isShareOfSegmentPlot = (
  plot:
    | Pick<
        PlotOptions,
        | 'chartType'
        | 'defaultMetricOptions'
        | 'secondaryDimension'
        | 'shareOfSegment'
      >
    | undefined
): boolean => {
  const metric = plot?.defaultMetricOptions?.[0];
  return (
    plot?.secondaryDimension !== undefined &&
    (plot.chartType === 'share-of-segment' ||
      (plot.chartType === 'line' &&
        plot.shareOfSegment === true &&
        metric === 'headcount'))
  );
};

/** A share-of-segment plot whose data follows its segment dropdown's picks. */
export const plotUsesShareOfSegmentPageSelections = (
  item: PositionedItem
): boolean =>
  isShareOfSegmentPlot(item.plot) &&
  item.plot?.secondaryDimension !== undefined &&
  isCompositionSecondaryDimension(item.plot.secondaryDimension);

export const isDynamicDivergingBarComboPlot = (
  plot: PlotOptions | undefined
): boolean =>
  plot?.chartType === 'diverging-bar-combo' &&
  (plot.divergingBarCombo?.dynamicEntityComparison === true ||
    !!plot.divergingBarCombo?.subfilterDimension);

export type PositionedItemInfo = {
  text: string;
  hyperlinks?: {
    text: string;
    href: string;
  }[];
};

export type ItemAlign = 'left' | 'center' | 'right';

export type PositionedItem = {
  position: Position;
  id: string;
  slotId?: string;
  title: string | null;
  isTitleManuallySet?: boolean;
  isTitleCentered?: boolean;
  align?: ItemAlign;
  hideHeader?: boolean;
  plot?: PlotOptions;
  info?: PositionedItemInfo;
};
